import { useState } from "react";
import {
  Input,
  Button,
  Card,
  Tabs,
  Table,
  Tag,
  Space,
  Empty,
  Typography,
  Row,
  Col,
  Badge,
  Divider,
  Collapse,
  Descriptions,
  Avatar,
  Modal,
  List,
  Alert
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ShopOutlined,
  HistoryOutlined,
  FilePdfOutlined,
  BankOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  FileProtectOutlined,
  ExportOutlined,
  EyeOutlined,
  DollarOutlined,
  AppstoreOutlined,
  LinkOutlined,
  ExclamationCircleFilled
} from "@ant-design/icons";
import { InfoKBISService } from "../../services/InfoKBIS_service";
import "../../css/InfoKBIS.css";
import LoaderAnnonce from "../../components/LoaderAnnonce";
import { ToastContainer } from "react-toastify";
import { trackMenuClick } from "../../services/menuClick_service";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Search } = Input;

interface BodaccAnnouncement {
  publicationavis: string;
  numeroannonce: string;
  commercant: string;
  typeavis_lib: string;
  familleavis_lib: string;
  tribunal: string;
  url_complete: string;
}

const ALL_TABS = [
  { key: "info", label: "Informations générales", icon: <InfoCircleOutlined /> },
  { key: "comptes", label: "Comptes annuels", icon: <FileTextOutlined /> },
  { key: "actes", label: "Actes", icon: <FileProtectOutlined /> },
  { key: "bodacc", label: "BODACC", icon: <FilePdfOutlined /> },
  { key: "historique", label: "Historique", icon: <HistoryOutlined /> },
  { key: "inpi", label: "Extrait INPI", icon: <DownloadOutlined /> },
  { key: "siren", label: "Avis de situation SIREN", icon: <BankOutlined /> },
];

const ModernInfoKBIS = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("info");
  const [modalVisible, setModalVisible] = useState(false);
  const userStatus = localStorage.getItem("userStatus") || "anonymous";
  const role = localStorage.getItem("role") || "CLIENT";

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    trackMenuClick('kbis-recherche', `Recherche société : ${searchQuery.trim()}`);
    try {
      setLoading(true);
      setError(null);
      setCompanies([]);
      setSelectedCompany(null);

      const encodedQuery = encodeURIComponent(searchQuery.trim());
      const result = await InfoKBISService.fetchCompanyInfo(encodedQuery);

      let resultsArray: any[] = [];

      if (Array.isArray(result)) {
        resultsArray = result;
      } else if (result) {
        resultsArray = [result];
      }

      if (resultsArray.length === 0) {
        setError("Aucune entreprise trouvée.");
      } else {
        setCompanies(resultsArray);

        if (resultsArray.length === 1) {
          const fullData = await InfoKBISService.fetchCompanyInfo(resultsArray[0].siren);
          setSelectedCompany(fullData);
          setModalVisible(true);
        }
      }
    } catch (err: any) {
      setError(err.message || "Erreur lors de la récupération des données.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (company: any) => {
    setLoading(true);
    try {
      const fullData = await InfoKBISService.fetchCompanyInfo(company.siren);
      setSelectedCompany(fullData);
      setActiveTab("info");
      setModalVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const renderInfoTab = () => {
    if (!selectedCompany) return null;

    return (
      <div className="kbis-info-container">
        <Card className="kbis-main-info-card" bordered={false}>
          <div className="kbis-company-info-box">
            <div className="kbis-company-header">
              <Avatar
                size={64}
                icon={<BankOutlined />}
                className="kbis-company-avatar-large"
              />
              <div className="kbis-company-title-section">
                <Title level={3} className="kbis-company-title">
                  {selectedCompany.companyName || "-"}
                </Title>
                <Space size="middle" wrap>
                  <Tag color="#4BB0C0" style={{ fontSize: 14 }}>
                    SIREN: {selectedCompany.siren}
                  </Tag>
                  {selectedCompany.siret && (
                    <Tag color="#2A919D" style={{ fontSize: 14 }}>
                      SIRET: {selectedCompany.siret}
                    </Tag>
                  )}
                </Space>
              </div>
            </div>

            <div className="kbis-info-details">
              <div className="kbis-info-item">
                <div className="kbis-info-item-label">
                  <EnvironmentOutlined /> Adresse
                </div>
                <div className="kbis-info-item-content">
                  {selectedCompany.address || "-"}
                </div>
              </div>

              <div className="kbis-info-item">
                <div className="kbis-info-item-label">
                  <UserOutlined /> Dirigeant
                </div>
                <div className="kbis-info-item-content">
                  {`${selectedCompany.firstName || ""} ${selectedCompany.lastName || ""}`.trim() || "-"}
                </div>
              </div>

              <div className="kbis-info-item">
                <div className="kbis-info-item-label">
                  <BankOutlined /> Forme juridique
                </div>
                <div className="kbis-info-item-content">
                  {selectedCompany.legalForm || "-"}
                </div>
              </div>

              <div className="kbis-info-item">
                <div className="kbis-info-item-label">
                  <DollarOutlined /> Capital
                </div>
                <div className="kbis-info-item-content">
                  {selectedCompany.capital
                    ? `${selectedCompany.capital.toLocaleString()} €`
                    : "-"}
                </div>
              </div>

              <div className="kbis-info-item">
                <div className="kbis-info-item-label">
                  <AppstoreOutlined /> Activité principale
                </div>
                <div className="kbis-info-item-content">
                  {selectedCompany.activities && selectedCompany.activities.length > 0
                    ? selectedCompany.activities.find((a: any) => a.indicateurPrincipal)?.descriptionDetaillee || "-"
                    : "-"}
                </div>
              </div>
            </div>

          </div>
        </Card>

        <Divider orientation="left">
          <Title level={4} className="kbis-section-title">
            <ShopOutlined /> ÉTABLISSEMENTS
          </Title>
        </Divider>

        {selectedCompany.etablissements && selectedCompany.etablissements.length > 0 ? (
          <Row gutter={[16, 16]}>
            {selectedCompany.etablissements.map((etab: any, idx: number) => {
              const isOpen = !etab.date_fermeture;
              return (
                <Col xs={24} sm={24} md={12} lg={12} xl={12} key={idx}>
                  <Card
                    className="kbis-etablissement-card"
                    bordered={false}
                  >
                    <div className="kbis-etablissement-status">
                      <Badge
                        color={isOpen ? "#2A919D" : "#ff4d4f"}
                        text={
                          <Text strong style={{ color: isOpen ? "#2A919D" : "#ff4d4f" }}>
                            {isOpen ? "En activité" : "Fermé"}
                          </Text>
                        }
                      />
                    </div>

                    <Title level={5} className="kbis-etablissement-name">
                      {etab.nom_commercial || "Aucun nom commercial"}
                    </Title>

                    <div className="kbis-etablissement-info">
                      <div className="kbis-info-row">
                        <Text strong className="kbis-info-label">SIRET:</Text>
                        <Text>{etab.siret || "-"}</Text>
                      </div>
                      <div className="kbis-info-row">
                        <Text strong className="kbis-info-label">Enseigne:</Text>
                        <Text>{etab.enseigne || "-"}</Text>
                      </div>
                      <div className="kbis-info-row">
                        <Text strong className="kbis-info-label">Adresse:</Text>
                        <Text>{etab.adresse || "-"}</Text>
                      </div>
                      <div className="kbis-info-row">
                        <Text strong className="kbis-info-label">Code Postal:</Text>
                        <Text>{etab.code_postal || "-"}</Text>
                      </div>
                      <div className="kbis-info-row">
                        <Text strong className="kbis-info-label">Ville:</Text>
                        <Text>{etab.ville || "-"}</Text>
                      </div>
                      <div className="kbis-info-row">
                        <Text strong className="kbis-info-label">Date création:</Text>
                        <Text type="secondary">{etab.date_creation || "-"}</Text>
                      </div>
                      {etab.date_fermeture && (
                        <div className="kbis-info-row">
                          <Text strong className="kbis-info-label">Date fermeture:</Text>
                          <Text>{etab.date_fermeture}</Text>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Empty description="Aucun établissement disponible" />
        )}
      </div>
    );
  };

  const renderComptesTab = () => {
    if (!selectedCompany?.annualAccounts?.length) {
      return <Empty description="Aucun compte annuel disponible" />;
    }

    return (
      <Collapse defaultActiveKey={["0"]} className="kbis-collapse">
        {selectedCompany.annualAccounts.map((bilan: any, idx: number) => (
          <Panel
            header={
              <Space>
                <CalendarOutlined />
                <Text strong>Date de Clôture: {bilan.dateCloture}</Text>
              </Space>
            }
            key={idx}
          >
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Référence du document">
                {bilan.nomDocument}
              </Descriptions.Item>
              <Descriptions.Item label="Date de dépôt">
                {bilan.dateDepot}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="#4BB0C0">{bilan.typeBilan}</Tag>
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                href={bilan.downloadUrl}
                target="_blank"
                className="kbis-btn-primary"
              >
                Télécharger
              </Button>
            </div>
          </Panel>
        ))}
      </Collapse>
    );
  };

  const renderActesTab = () => {
    if (!selectedCompany?.attachments?.length) {
      return <Empty description="Aucun acte disponible" />;
    }

    const columns = [
      {
        title: "Date de dépôt",
        dataIndex: "dateDepot",
        key: "dateDepot",
        render: (date: string) => new Date(date).toLocaleDateString(),
      },
      {
        title: "Nom du document",
        dataIndex: "nomDocument",
        key: "nomDocument",
        render: (text: string) => text.replace(/_/g, " "),
      },
      {
        title: "Téléchargement",
        dataIndex: "downloadUrl",
        key: "downloadUrl",
        render: (url: string) => (
          <Button
            type="link"
            icon={<FilePdfOutlined style={{ color: '#eb8635', fontSize: 20 }} />}
            href={url}
            target="_blank"
          />
        ),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={selectedCompany.attachments}
        pagination={{ pageSize: 10 }}
        className="kbis-table"
      />
    );
  };

  const renderBodaccTab = () => {
    if (!selectedCompany?.bodaccAnnouncements?.length) {
      return <Empty description="Aucune annonce commerciale disponible" />;
    }

    return (
      <Row gutter={[16, 16]}>
        {selectedCompany.bodaccAnnouncements.map((annonce: BodaccAnnouncement, idx: number) => (
          <Col xs={24} sm={12} lg={8} key={idx}>
            <Card
              className="kbis-bodacc-card"
              title={annonce.typeavis_lib}
              extra={<Tag color="#4BB0C0">{annonce.numeroannonce}</Tag>}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Commerçant">
                  {annonce.commercant}
                </Descriptions.Item>
                <Descriptions.Item label="Famille">
                  {annonce.familleavis_lib}
                </Descriptions.Item>
                <Descriptions.Item label="Tribunal">
                  {annonce.tribunal}
                </Descriptions.Item>
              </Descriptions>
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<ExportOutlined />}
                  href={annonce.url_complete}
                  target="_blank"
                  block
                  className="kbis-btn-primary"
                >
                  Voir l'annonce
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderHistoriqueTab = () => {
    if (!selectedCompany?.historique?.length) {
      return <Empty description="Aucun historique disponible" />;
    }

    const columns = [
      {
        title: "Date d'intégration",
        dataIndex: ["details", "dateIntegration"],
        key: "dateIntegration",
        render: (date: string) => date ? new Date(date).toLocaleDateString() : "—",
      },
      {
        title: "Code évènement",
        dataIndex: ["details", "codeEvenement"],
        key: "codeEvenement",
        render: (text: string) => text || "—",
      },
      {
        title: "Libellé évènement",
        dataIndex: ["details", "libelleEvenement"],
        key: "libelleEvenement",
        render: (text: string) => text || "—",
      },
      {
        title: "Date d'effet",
        dataIndex: ["details", "dateEffet"],
        key: "dateEffet",
        render: (date: string) => date ? new Date(date).toLocaleDateString() : "—",
      },
      {
        title: "Numéro liasse",
        dataIndex: ["details", "numeroLiasse"],
        key: "numeroLiasse",
        render: (text: string) => text || "—",
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={selectedCompany.historique}
        pagination={{ pageSize: 10 }}
        className="kbis-table"
        scroll={{ x: 800 }}
      />
    );
  };

  const renderInpiTab = () => {
    return (
      <Card className="kbis-download-card">
        {selectedCompany?.rcs || selectedCompany?.siren ? (
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <FilePdfOutlined style={{ fontSize: 64, color: '#4BB0C0' }} />
            <Title level={4}>Extrait INPI</Title>
            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              href={`https://data.inpi.fr/export/companies?format=pdf&ids=${selectedCompany.rcs || selectedCompany.siren}`}
              target="_blank"
              className="kbis-btn-primary"
            >
              Télécharger l'extrait INPI
            </Button>
          </Space>
        ) : (
          <Empty description="Aucun identifiant INPI disponible pour ce dossier" />
        )}
      </Card>
    );
  };

  const renderSirenTab = () => {
    return (
      <Card className="kbis-download-card">
        {selectedCompany?.siret ? (
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <BankOutlined style={{ fontSize: 64, color: '#2A919D' }} />
            <Title level={4}>Avis de situation SIREN</Title>
            <Button
              size="large"
              icon={<DownloadOutlined />}
              href={`https://api-avis-situation-sirene.insee.fr/identification/pdf/${selectedCompany.siret}`}
              target="_blank"
              className="kbis-btn-primary"
            >
              Télécharger l'avis de situation SIREN
            </Button>
          </Space>
        ) : (
          <Empty description="Aucun SIRET disponible" />
        )}
      </Card>
    );
  };

  const renderProceduresTab = () => {
    return (
      <Card className="kbis-download-card">
        {selectedCompany?.rcs || selectedCompany?.siren ? (
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <LinkOutlined style={{ fontSize: 64, color: '#4BB0C0' }} />
            <Title level={4}>Accès aux formalités INPI</Title>
            <Button
              type="primary"
              size="large"
              icon={<LinkOutlined />}
              href="https://procedures.inpi.fr/?/"
              target="_blank"
              rel="noopener noreferrer"
              className="kbis-btn-primary"
            >
              Veuillez visiter le site des formalités INPI
            </Button>
          </Space>
        ) : (
          <Empty description="Aucun identifiant disponible pour accéder aux formalités" />
        )}
      </Card>
    );
  };

  if (userStatus === "anonymous") {
          return (
              <>
                  <ToastContainer />
                  <div className="gp-root container-fluid">
                      <div className="mt-5 text-center">
                          <Alert
                              className="custom-alert-white"
                              message="Module d'extrait INPI indisponible"
                              description="Ravi de vous compter parmi nous ! Publiez votre première annonce et finalisez votre inscription pour profiter de ce module."
                              type="warning"
                              showIcon
                              icon={<ExclamationCircleFilled style={{ color: '#eb8635' }} />}
                              style={{
                                  fontSize: '1.1rem',
                              }}
                          />
                      </div>
                  </div>
              </>
          );
      }

  return (
    <div className="kbis-modern-container">
      {/* Header */}
      <div className="kbis-header">
        <div className="kbis-header-content">
          <div style={{ position: 'relative', minHeight: '100px' }}>
            {/* Bouton à droite absolument positionné */}
            {role ==="CLIENT" && <Button 
              href="https://procedures.inpi.fr/?"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackMenuClick('kbis-deposer-documents', 'Ici, déposez vos demandes à l\'INPI')}
              style={{ position: 'absolute', right: 0, top: 0,textDecoration:"none" ,backgroundColor :"#f4a261", color:"white", padding:"10px 15px" }}
            >
             <span style={{color:"white"}}>Ici, déposez vos demandes à l'INPI</span>
            </Button>
            }
            
            {/* Contenu centré */}
            <div style={{ textAlign: 'center' }}>
              <Title level={1} className="kbis-header-title">
                Extrait des Sociétés
              </Title>
              <Text className="kbis-header-subtitle">
                Recherchez et consultez les informations légales des entreprises françaises
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="kbis-search-section">
        <Row justify="center">
          <Col xs={24} sm={20} md={16} lg={12}>
            <Search
              placeholder="Rechercher par SIRET, SIREN, DENOMINATION..."
              size="large"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={handleSearch}
              enterButton={
                <Button icon={<SearchOutlined />}>
                  Rechercher
                </Button>
              }
              loading={loading}
              className="kbis-search-input"
            />
          </Col>
        </Row>
      </div>

      {/* Error Message */}
      {error && (
        <div className="kbis-error-container">
          <Card className="kbis-error-card">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<Text type="danger">{error}</Text>}
            />
          </Card>
        </div>
      )}

      {/* Loading */}
      {loading && !modalVisible && (
        <div className="kbis-loading-container">
          <LoaderAnnonce />
        </div>
      )}

      {/* Results List */}
      {companies.length > 1 && !loading && (
        <div className="kbis-results-section">
          <Card className="kbis-results-card" bordered={false}>
            <Title level={4} className="kbis-results-title">
              {companies.length} résultat{companies.length > 1 ? 's' : ''} trouvé{companies.length > 1 ? 's' : ''}
            </Title>
            <List
              itemLayout="horizontal"
              dataSource={companies}
              renderItem={(company: any) => (
                <List.Item
                  className="kbis-list-item"
                  actions={[
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => handleSelectCompany(company)}
                      className="kbis-btn-primary"
                    >
                      Voir détails
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size={48}
                        icon={<BankOutlined />}
                        className="kbis-company-avatar"
                      />
                    }
                    title={
                      <Text strong className="kbis-list-company-name">
                        {company.companyName || "-"}
                      </Text>
                    }
                    description={
                      <Space size="middle" wrap>
                        <Tag color="#4BB0C0">SIREN: {company.siren}</Tag>
                        {company.legalForm && (
                          <Text type="secondary">{company.legalForm}</Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </div>
      )}

      {/* Modal for Company Details */}
      <Modal
        title={null}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        className="kbis-modal"
        destroyOnClose
      >
        {selectedCompany && (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            size="large"
            className="kbis-tabs"
          >
            <TabPane
              tab={<span><InfoCircleOutlined /> Informations générales</span>}
              key="info"
            >
              {renderInfoTab()}
            </TabPane>
            <TabPane
              tab={<span><FileTextOutlined /> Comptes annuels</span>}
              key="comptes"
            >
              {renderComptesTab()}
            </TabPane>
            <TabPane
              tab={<span><FileProtectOutlined /> Actes</span>}
              key="actes"
            >
              {renderActesTab()}
            </TabPane>
            <TabPane
              tab={<span><FilePdfOutlined /> BODACC</span>}
              key="bodacc"
            >
              {renderBodaccTab()}
            </TabPane>
            <TabPane
              tab={<span><HistoryOutlined /> Historique</span>}
              key="historique"
            >
              {renderHistoriqueTab()}
            </TabPane>
            <TabPane
              tab={<span><DownloadOutlined /> Extrait INPI</span>}
              key="inpi"
            >
              {renderInpiTab()}
            </TabPane>
            <TabPane
              tab={<span><BankOutlined /> Avis de situation SIREN</span>}
              key="siren"
            >
              {renderSirenTab()}
            </TabPane>
            {/* Lien vers procedures */}
            <TabPane
              tab={<span><LinkOutlined /> Accès INPI Formalités</span>}
              key="procedures"
            >
              {renderProceduresTab()}
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default ModernInfoKBIS;